import {
	getDropdownSelectedSingle,
	ReactiveClienteFieldFactory,
	ReactiveDomainSectionFactory,
	ReactiveObservacionesFieldFactory,
	ReactiveProductoFieldFactory,
	ReactiveSelectionResolver,
	type ReactiveSelectionResolverConfiguration,
} from '@fitogest/reactive-form-builder/utils';
import {
    Form,
    FormButton,
    FormField,
    FormSection,
    FormType,
    InputType,
    ReactiveFormChangedField,
    ReactiveFormValue,
    type ReactiveDropdownDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import { FincasEnvironmentDefinition } from '../environment-variables';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { FincasModel } from '../../../../../models/form-common/fincas.module';
import { ClienteModel } from 'src/app/models/cliente.model';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { environment } from 'src/environments/environment';
import { DropdownLists } from 'src/app/common/dropdownLists';

export interface FincasFormActions {
	openReaImport?: () => void;
}

export interface FincasDropdownEndpoints {
	readonly clientes?: unknown;
	readonly productos?: unknown;
}

const CLIENT_SELECTION_CONFIGURATION: ReactiveSelectionResolverConfiguration = {
	idKeys: ['id', 'id_cliente', 'nombre', 'razon_social'],
};

export class FincasFormDefinition {
	private readonly formType!: FormType;

	public readonly env: FincasEnvironmentDefinition = new FincasEnvironmentDefinition();

	public readonly lists: DropdownLists = new DropdownLists();

	public readonly user = StorageManager.getUser();

	public model: ReactiveFormValue<FincasModel> = {} as ReactiveFormValue<FincasModel>;

	public lastChangedFieldName: ReactiveFormChangedField<FincasModel> = '';

	public formFields!: Form<FincasModel>;

	public fieldNames: FormField<FincasModel>[] = [];

	private getSelectedClienteValue(): ClienteModel | null {
		const selectedClient = getDropdownSelectedSingle(this.lists.clientes);

		if (selectedClient !== null) {
			return selectedClient;
		}

		return ReactiveSelectionResolver.resolveValueById(
			this.lists.clientes,
			this.model.id_cliente,
			CLIENT_SELECTION_CONFIGURATION,
		);
	}

	private getSelectedClienteText(fieldName: keyof ClienteModel): string | null {
		const value = this.getSelectedClienteValue()?.[fieldName];

		if (value === null || value === undefined) {
			return null;
		}

		return typeof value === 'string' ? value : String(value);
	}

	public fieldLabels: string[] = [];

	/**
	 * Creates a finca form definition with its initial metadata, sections and actions.
	 *
	 * This constructor stores the requested form type, resolves the visible form
	 * action label and builds the complete `formFields` configuration used by the
	 * reactive form builder.
	 *
	 * @constructor
	 * @public
	 *
	 * @param type - Request type used to define the form behaviour.
	 * @param table - Optional table configuration used by table-based form modes.
	 * The first item is assigned as table data and the second item as table style.
	 * @param actions - Optional callbacks available to the form definition.
	 *
	 * @returns A new finca form definition instance.
	 *
	 * @remarks
	 * When the form type is `FormType.CREATION`, the header uses the `Crear` action.
	 * For any other form type, the header uses the `Actualizar` action.
	 *
	 * The `actions` parameter defaults to an empty object so callers can instantiate
	 * the definition without providing custom form actions.
	 *
	 * @example
	 * ```ts
	 * const form = new FincasFormDefinition(FormType.CREATION, null);
	 * ```
	 *
	 * @see FormType
	 * @see FincasFormActions
	 * @see FormType
	 * @see Form
	 * @see FormSection
	 * @see FincasEnvironmentDefinition
	 */
	constructor(
		type: FormType,
		table: (string[] | string[][])[] | null,
		private readonly actions: FincasFormActions = {},
		private readonly dropdownEndpoints: FincasDropdownEndpoints = {},
	) {
		this.formType = +type;
		const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

		this.formFields = {
			name: 'fincas',
			header: action + ' ' + this.env.fincaName,
			type: this.formType,
			sections: this.getFormSections(),
			table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
			uploadButton: {
				isEnabled: false,
				label: '',
			},
			buttons: this.getHeaderButtons(),
			signed: false,
		};
	}// constructor();

	private createClientesDataSourceField(): Partial<Pick<FormField<FincasModel>, 'dataSource'>> {
		const endpoint = this.dropdownEndpoints.clientes;

		if (endpoint === undefined || endpoint === null) {
			return {};
		}

		const dataSource: ReactiveDropdownDataSource<FincasModel> = {
			endpoint,
			cacheKey: 'fincas-clientes',
			distinctBy: 'id',
			cacheEmptyResults: false,
			mapOption: (item: unknown) => {
				const cliente = item as Partial<ClienteModel>;
				const label = `${cliente.nombre ?? ''} ${cliente.apellidos ?? ''} ${cliente.apellido_1 ?? ''} ${cliente.apellido_2 ?? ''}`
					.trim() || String(cliente.id ?? '');

				return {
					label,
					value: item as ClienteModel,
					raw: item,
				};
			},
		};

		return { dataSource };
	}

	private createProductosDataSourceField(): Partial<Pick<FormField<FincasModel>, 'dataSource'>> {
		const endpoint = this.dropdownEndpoints.productos;

		if (!this.env.showFincasProductos || endpoint === undefined || endpoint === null) {
			return {};
		}

		const dataSource: ReactiveDropdownDataSource<FincasModel> = {
			endpoint,
			cacheKey: 'fincas-productos-fertirrigacion',
			params: { tipo: 'fertilizante' },
			labelKey: 'nombre',
			valueKey: 'id',
			distinctBy: 'id',
			cacheEmptyResults: false,
			mapOption: (item: unknown) => {
				const producto = item as Partial<ProductosModel>;

				return {
					label: String(producto.nombre ?? producto.id ?? ''),
					value: item as ProductosModel,
					raw: item,
				};
			},
		};

		return { dataSource };
	}

	/**
	 * Builds and returns the finca form field definitions.
	 *
	 * This method defines the full list of configurable fields used by the finca
	 * form, including labels, input types, widths, visibility rules, validation
	 * requirements, linked fields and default values.
	 *
	 * @public
	 *
	 * @returns The finca form fields sorted by their configured display order.
	 *
	 * @remarks
	 * Some fields are conditionally visible depending on the active environment,
	 * enabled features, application name, REA mode or client support.
	 *
	 * Visible fields are also appended to `fieldNames` and their labels are appended
	 * to `fieldLabels`, allowing the same definition to be reused by export or table
	 * generation logic.
	 *
	 * Several fields are linked to `id_cliente` when client management is enabled.
	 * In those cases, their values can be automatically updated from the selected
	 * client through the `updateValue` callback.
	 *
	 * @example
	 * ```ts
	 * const fields = this.getFormFields();
	 * ```
	 *
	 * @see FormField
	 * @see FincasModel
	 * @see InputType
	 * @see StorageManager.getClient
	 * @see environment.features
	 * @see DropdownLists
	 */
	public getFormFields(): FormField<FincasModel>[] {
		const formFields: FormField<FincasModel>[] = [
			ReactiveClienteFieldFactory.create<FincasModel>({
				...this.createClientesDataSourceField(),
				label: this.env.clienteName,
				data: this.lists.clientes,
				defaultValue: StorageManager.getClient()?.id !== undefined ? StorageManager.getClient()?.id : null,
				conditions: {
					rememberedOnDuplicate: true,
					required: true,
					visible: this.env.hasClients,
				},
				order: 1,
			}),
			{
				name: 'razon_social',
				label: 'Razón social',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.showFincasRazonSocial,
				},
				order: 2,
			},
			{
				name: 'nombre',
				label: 'Nombre de ' + this.env.fincaName,
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: true,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 3,
			},
			{
				name: 'nif',
				label: 'NIF',
				input: {
					type: this.env.activateREA ? InputType.ONLY_TEXT : InputType.TEXT,
					defaultValue: !this.env.hasClients ? this.env.user.nif : '-',
					isEditable: true,
				},
				updateValue: () => this.getSelectedClienteText('nif'),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: this.env.fincasNifRequired || this.env.activateREA,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: environment.features.hasClients ? 1 : 4,
			},
			{
				name: 'codigo',
				label: 'Código',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.showFincasCodigo,
				},
				order: 5,
			},
			{
				name: 'registro_nacional',
				label: 'Registro Nacional',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName !== 'gardengest' && !this.env.activateREA,
				},
				order: 6,
			},
			{
				name: 'registro_autonomico',
				label: 'Registro Autonómico',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName !== 'gardengest' && !this.env.activateREA,
				},
				order: 7,
			},
			{
				name: 'codigo_REA',
				label: 'REA',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.activateREA,
				},
				order: 8,
			},
			{
				name: 'direccion',
				label: 'Dirección',
				input: {
					type: this.env.activateREA && this.env.hasClients ?
						InputType.ONLY_TEXT :
						InputType.TEXT,
						isEditable: true
				},
				updateValue: () => this.getSelectedClienteText('direccion'),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 9,
			},
			{
				name: 'localidad',
				label: 'Localidad',
				input: {
					type: this.env.activateREA && this.env.hasClients ?
						InputType.ONLY_TEXT :
						InputType.TEXT,
						isEditable: true
					},
				updateValue: () => this.getSelectedClienteText('localidad'),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 10,
			},
			{
				name: 'codigo_postal',
				label: 'Código Postal',
				input: {
					type: this.env.activateREA && this.env.hasClients ?
						InputType.ONLY_TEXT :
						InputType.TEXT,
					isEditable: true,
				},
				updateValue: () => this.getSelectedClienteText('codigo_postal'),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 11,
			},
			{
				name: 'provincia',
				label: 'Provincia',
				input: {
					type: this.env.activateREA && this.env.hasClients ?
						InputType.ONLY_TEXT :
						InputType.TEXT,
						isEditable: true
					},
				updateValue: () => this.getSelectedClienteText('provincia'),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 12,
			},
			{
				name: 'telefono',
				label: 'Teléfono',
				input: {
					type: environment.features.activateREA ? InputType.ONLY_TEXT : InputType.NUMERIC,
					defaultValue: !this.env.hasClients ? this.env.user.telefono : '-',
					isEditable: true,
				},
				updateValue: () => this.getSelectedClienteText('telefono'),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 13,
			},
			{
				name: 'movil',
				label: 'Móvil',
				input: { type: this.env.activateREA ? InputType.ONLY_TEXT : InputType.NUMERIC, isEditable: true },
				updateValue: () => this.getSelectedClienteText('telefono'),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: !environment.features.activateREA,
				},
				order: 14,
			},
			{
				name: 'email',
				label: 'Email',
				input: {
					type: this.env.activateREA ? InputType.ONLY_TEXT : InputType.TEXT,
					defaultValue: !this.env.hasClients ? this.env.user.email : '-',
					isEditable: true
				},
				updateValue: () => this.getSelectedClienteText('email'),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 15,
			},
			{
				name: 'superficie',
				label: 'Superficie',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName !== 'avafitogest',
				},
				order: 16,
			},
			{
				name: 'tipo_explotacion',
				label: 'Tipo de explotación',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: this.lists.tipos,
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.showTipoExplotacion,
				},
				order: 17,
			},
			{
				name: 'titular_tipo',
				label: 'Tipo Representación',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.activateREA,
				},
				order: 18,
			},
			{
				name: 'alquilado',
				label: 'Alquilado',
				input: { type: InputType.SWITCH, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'covidai',
				},
				order: 19,
			},
			ReactiveProductoFieldFactory.create<FincasModel>({
				...this.createProductosDataSourceField(),
				name: 'ids_productos',
				label: 'Productos (Fertirrigación)',
				type: InputType.MULTISELECT,
				data: this.lists.productos,
				conditions: {
					rememberedOnDuplicate: true,
					visible: this.env.showFincasProductos,
				},
				order: 20,
			}),
			ReactiveObservacionesFieldFactory.create<FincasModel>({
				label: 'Observaciones Cliente',
				conditions: {
					rememberedOnDuplicate: true,
					visible: this.env.appName === 'gardengest',
				},
				order: 21,
			}),
			{
				name: 'propiedad',
				label: 'Propiedad',
				input: {
					type: InputType.DROPDOWN,
					isEditable: true,
					data: this.lists.propiedad,
				},
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'agropecuariaramirogruas',
				},
				order: 22,
			},
			{
				name: 'ccc',
				label: 'CCC',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: this.env.appName === 'campanaragricola',
				},
				order: 22,
			},
		];

		this.fieldNames = this.fieldNames.concat(formFields.filter((it) => it.conditions.isVisible));
		this.fieldLabels = this.fieldLabels.concat(
			formFields.filter((it) => it.conditions.isVisible).map((it) => it.label),
		);
		return formFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}// ();

	/**
	 * Builds and returns the titular form field definitions.
	 *
	 * This method defines the owner or representative fields associated with a finca,
	 * including personal, contact and representation data. Field visibility is
	 * controlled by the received section visibility flag.
	 *
	 * @public
	 *
	 * @param isSectionVisible - Indicates whether the titular section fields should
	 * be visible in the generated form definition.
	 *
	 * @returns The titular form fields sorted by their configured display order.
	 *
	 * @remarks
	 * Visible fields are appended to `fieldNames` and their labels are appended to
	 * `fieldLabels`, allowing the same metadata to be reused by export or table
	 * generation logic.
	 *
	 * The `titular_direccion` field changes its order for the `agrogestSEIX`
	 * application, while `titular_telefono` is editable only when the active
	 * application is not `agrogestSIEX`.
	 *
	 * @example
	 * ```ts
	 * const titularFields = this.getTitularFields(true);
	 * ```
	 *
	 * @see FormField
	 * @see FincasModel
	 * @see InputType
	 * @see environment.appName
	 */
	public getTitularFields(isSectionVisible: boolean): FormField<FincasModel>[] {
		const titularFields: FormField<FincasModel>[] = [
			{
				name: 'titular_nombre',
				label: 'Titular Nombre',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 1,
			},
			{
				name: 'titular_nif',
				label: 'Titular NIF',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 2,
			},
			{
				name: 'titular_direccion',
				label: 'Titular Dirección',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: this.env.appName === 'agrogestSEIX' ? 2 : 3,
			},
			{
				name: 'titular_localidad',
				label: 'Titular Localidad',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 3,
			},
			{
				name: 'titular_codigo_postal',
				label: 'Titular Código Postal',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 4,
			},
			{
				name: 'titular_provincia',
				label: 'Titular Provincia',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 5,
			},
			{
				name: 'titular_telefono',
				label: 'Titular Teléfono',
				input: { type: InputType.TEXT, isEditable: environment.appName !== 'agrogestSIEX' },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 6,
			},
			{
				name: 'titular_email',
				label: 'Titular Email',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 7,
			},
			{
				name: 'titular_tipo',
				label: 'Tipo Representación',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 8,
			},
		];

		this.fieldNames = this.fieldNames.concat(titularFields.filter((it) => it.conditions.isVisible));
		this.fieldLabels = this.fieldLabels.concat(
			titularFields.filter((it) => it.conditions.isVisible).map((it) => it.label),
		);
		return titularFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}// ();

	/**
	 * Builds and returns the representative form field definitions.
	 *
	 * This method defines the representative fields associated with a finca,
	 * including name, NIF and email. Field visibility is controlled by the received
	 * section visibility flag.
	 *
	 * @public
	 *
	 * @param isSectionVisible - Indicates whether the representative section fields
	 * should be visible in the generated form definition.
	 *
	 * @returns The representative form fields sorted by their configured display order.
	 *
	 * @remarks
	 * Visible fields are appended to `fieldNames` and their labels are appended to
	 * `fieldLabels`, allowing the same metadata to be reused by export or table
	 * generation logic.
	 *
	 * Representative fields are editable only when the active application is not
	 * `agrogestSIEX`.
	 *
	 * @example
	 * ```ts
	 * const representanteFields = this.getRepresentanteFields(true);
	 * ```
	 *
	 * @see FormField
	 * @see FincasModel
	 * @see InputType
	 * @see environment.appName
	 */
	public getRepresentanteFields(isSectionVisible: boolean): FormField<FincasModel>[] {
		const representanteFields: FormField<FincasModel>[] = [
			{
				name: 'representante_nombre',
				label: 'Representante Nombre',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 1,
			},
			{
				name: 'representante_nif',
				label: 'Representante NIF',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 2,
			},
			{
				name: 'representante_email',
				label: 'Representante Email',
				input: { type: InputType.TEXT, isEditable: true },
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: isSectionVisible,
				},
				order: 3,
			},
		];

		this.fieldNames = this.fieldNames.concat(representanteFields.filter((it) => it.conditions.isVisible));
		this.fieldLabels = this.fieldLabels.concat(
			representanteFields.filter((it) => it.conditions.isVisible).map((it) => it.label),
		);
		return representanteFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}// ();

	/**
	 * Builds and returns the hidden finca form field definitions.
	 *
	 * This method defines internal fields required by the form submission, such as
	 * the finca identifier and the assigned user identifier. These fields are always
	 * included in the form but are not intended to be edited by the user.
	 *
	 * @public
	 *
	 * @returns The hidden form fields sorted by their configured display order.
	 *
	 * @remarks
	 * The `id_usuario` value is resolved differently depending on the application
	 * type:
	 *
	 * - For `cropgest`, it uses the selected client's `id_usuario` or falls back to
	 *   the current user identifier.
	 * - For the rest of application types, it uses the selected client's assigned
	 *   user, the current model user or the current user identifier.
	 *
	 * Visible fields are appended to `fieldNames` and their labels are appended to
	 * `fieldLabels`, allowing the same metadata to be reused by export or table
	 * generation logic.
	 *
	 * @example
	 * ```ts
	 * const hiddenFields = this.getHiddenFields();
	 * ```
	 *
	 * @see FormField
	 * @see FincasModel
	 * @see InputType.HIDDEN
	 * @see StorageManager.getClient
	 * @see StorageManager.getUser
	 */
	public getHiddenFields(): FormField<FincasModel>[] {
		const hiddenFields: FormField<FincasModel>[] = [
			{
				name: 'id',
				label: '',
				input: {
					type: InputType.HIDDEN,
					defaultValue: this.model.id,
					isEditable: false,
				},
				width: 2,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 1,
			},
			{
				name: 'id_usuario',
				label: '',
				input: {
					isEditable: false,
					defaultValue:
						this.env.applicationType === 'cropgest'
							? (StorageManager.getClient()?.id_usuario ?? StorageManager.getUser().id)
							: (StorageManager.getClient()?.id_usuario_asignado ??
								this.model.id_usuario ??
								StorageManager.getUser().id),
					type: InputType.HIDDEN,
				},
				updateValue: () =>
					this.env.applicationType === 'cropgest'
						? (StorageManager.getClient()?.id_usuario ?? StorageManager.getUser().id)
						: (StorageManager.getClient()?.id_usuario_asignado ?? this.model.id_usuario ?? StorageManager.getUser().id),
				width: 3,
				conditions: {
					isRememberedOnDuplicate: true,
					isRequired: false,
					isRetained: false,
					hasUndoEnabled: false,
					isVisible: true,
				},
				order: 2,
			},
		];

		this.fieldNames = this.fieldNames.concat(hiddenFields.filter((it) => it.conditions.isVisible));
		this.fieldLabels = this.fieldLabels.concat(
			hiddenFields.filter((it) => it.conditions.isVisible).map((it) => it.label),
		);
		return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}// ();

	/**
	 * Builds and returns the finca form section definitions.
	 *
	 * This method groups the finca, titular, representative and hidden fields into
	 * ordered form sections according to the active environment configuration.
	 *
	 * @private
	 *
	 * @returns The finca form sections sorted by their configured display order.
	 *
	 * @remarks
	 * The main finca data section is always visible, but its title is hidden when
	 * REA mode is active.
	 *
	 * The titular section is visible only when REA mode is disabled, while the
	 * representative section is visible only when REA mode is enabled.
	 *
	 * The hidden section is always visible because it contains internal fields
	 * required for form submission.
	 *
	 * @example
	 * ```ts
	 * const sections = this.getFormSections();
	 * ```
	 *
	 * @see FormSection
	 * @see FincasModel
	 * @see getFormFields
	 * @see getTitularFields
	 * @see getRepresentanteFields
	 * @see getHiddenFields
	 */
	private getFormSections(): FormSection<FincasModel>[] {
		const sections: FormSection<FincasModel>[] = [
			ReactiveDomainSectionFactory.identificacion<FincasModel>({
				name: !this.env.activateREA ? 'Datos de ' + this.env.fincaName : '',
				fields: this.getFormFields(),
				order: 1,
			}),
			ReactiveDomainSectionFactory.datosAdministrativos<FincasModel>({
				name: 'Datos del ' + this.env.clienteName,
				visible: !this.env.activateREA,
				fields: this.getTitularFields(!this.env.activateREA),
				order: 2,
			}),
			ReactiveDomainSectionFactory.datosAdministrativos<FincasModel>({
				name: 'Datos del Representante',
				visible: this.env.activateREA,
				fields: this.getRepresentanteFields(this.env.activateREA),
				order: 3,
			}),
			ReactiveDomainSectionFactory.datosAdministrativos<FincasModel>({
				name: '',
				fields: this.getHiddenFields(),
				order: 4,
			}),
		];

		return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}// ();

	/**
	 * Builds and returns the header button definitions for the finca form.
	 *
	 * This method adds the REA import action button when REA mode is active. If REA
	 * mode is disabled, no header buttons are generated.
	 *
	 * @private
	 *
	 * @returns The header buttons available for the current form type.
	 *
	 * @remarks
	 * The `Importar REA` button is hidden for deletion and informative form modes.
	 * When clicked, it safely executes the optional `openReaImport` callback if it
	 * was provided through the form actions.
	 *
	 * @example
	 * ```ts
	 * const buttons = this.getHeaderButtons();
	 * ```
	 *
	 * @see FormButton
	 * @see FormType.DELETION
	 * @see FormType.INFORMATIVE
	 * @see FincasFormActions.openReaImport
	 */
	private getHeaderButtons(): FormButton[] {
		if (!this.env.activateREA) {
			return [];
		}// if();

		return [
			{
				type: 'callback',
				label: 'Importar REA',
				icon: 'fa fa-download',
				class: 'btn bg-primary text-white bg-gradient border-0 fw-bold',
				function: () => this.actions.openReaImport?.(),
				isVisible: this.formType !== FormType.DELETION && this.formType !== FormType.INFORMATIVE,
			},
		];
	}// ();
}// class;
